/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ProductCard } from '@/components/blocks/ProductCard';
import { useProduct } from '@/data/Content/Product';
import { useUser } from '@/data/User';
import { ContentContext } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { ProductType } from '@/data/types/Product';
import { getContractIdFromContext } from '@/utils/getContractIdFromContext';
import { Skeleton } from '@mui/material';
import { FC, useContext, useEffect } from 'react';

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}

export const ProductLite: FC<{
	product: ProductType;
	contract?: string;
	infoElectronico?: InfoElectronico;
	clickAction?: () => void;
}> = ({ product, contract, clickAction, infoElectronico }) => {
	const { onNotify } = useContext(ContentContext) as {
		onNotify: (id: ID, contract: string, product?: ProductType) => void;
	};

	useEffect(() => {
		if (onNotify && contract) {
			onNotify(product.partNumber, contract, product);
		}
	}, [contract, product, onNotify]);

	return (
		<ProductCard product={product} infoElectronico={infoElectronico} clickAction={clickAction} />
	);
};

export const Product: FC<{
	partNumber: string;
	infoElectronico?: InfoElectronico;
	clickAction?: () => void;
}> = ({ partNumber, clickAction, infoElectronico }) => {
	const { rawData, product, loading } = useProduct({ id: partNumber });
	const { user } = useUser();
	const contract = getContractIdFromContext(user?.context);
	const { onNotify } = useContext(ContentContext) as {
		onNotify: (id: ID, contract: string, product?: ProductType) => void;
	};

	useEffect(() => {
		if (onNotify && rawData) {
			onNotify(partNumber, contract, product);
		}
	}, [loading, rawData, product]); // eslint-disable-line react-hooks/exhaustive-deps

	if (loading && !product) {
		return <Skeleton variant="rectangular" width={290} height={440} />;
	}

	return loading || !product ? null : (
		<ProductCard product={product} infoElectronico={infoElectronico} clickAction={clickAction} />
	);
};
